import React from 'react';
import PropTypes from 'prop-types';
import {
  FileText,
  PlayCircle,
  HelpCircle,
  History,
  RefreshCw,
  Calendar,
  Download,
  CheckSquare
} from 'lucide-react';

/**
 * GapAnalysisHeader - Header section for Gap Analysis page
 * Includes historical banner, title, action buttons, and start assessment CTA
 * @param {boolean} viewingHistoricalAssessment - Whether viewing a historical assessment
 * @param {Object} assessment - Current assessment object
 * @param {function} onBackToCurrent - Handler to return to current assessment
 * @param {function} onViewHistory - Handler to view assessment history
 * @param {function} onNavigateToTasks - Handler to navigate to task manager
 * @param {function} onDownloadPDF - Handler to download PDF
 * @param {boolean} downloadingPDF - Whether PDF is currently being generated
 * @param {function} onRetakeAssessment - Handler to retake assessment
 * @param {function} onShowScoringGuide - Handler to show scoring guide
 * @param {function} onStartAssessment - Handler to start assessment
 * @param {number} totalQuestions - Total number of questions in assessment
 * @param {boolean} isViewer - Whether current user is a viewer (read-only)
 */
const GapAnalysisHeader = ({
  viewingHistoricalAssessment,
  assessment,
  onBackToCurrent,
  onViewHistory,
  onNavigateToTasks,
  onDownloadPDF,
  downloadingPDF,
  onRetakeAssessment,
  onShowScoringGuide,
  onStartAssessment,
  totalQuestions,
  isViewer = false
}) => {
  const hasAnswers = assessment && assessment.answered_questions > 0;

  return (
    <>
      {/* Historical Assessment Banner */}
      {viewingHistoricalAssessment && (
        <div className="mb-4 bg-amber-50 border-2 border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Calendar className="text-amber-600" size={24} />
            <div>
              <h3 className="font-semibold text-amber-900">Viewing Historical Assessment</h3>
              <p className="text-sm text-amber-700">
                Completed on {new Date(assessment.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          <button
            onClick={onBackToCurrent}
            className="flex items-center space-x-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition"
          >
            <RefreshCw size={18} />
            <span>Back to Current</span>
          </button>
        </div>
      )}

      {/* Header - Redesigned */}
      <div className="mb-6">
        {/* Title Section */}
        <div className="mb-4">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Business Gap Assessment</h1>
          <p className="text-lg text-gray-600">
            Assess your business attractiveness and readiness to identify and close gaps for maximum value
          </p>
        </div>

        {/* Action Buttons - Organized in Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <button
            onClick={onViewHistory}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-white border-2 border-purple-200 text-purple-700 rounded-lg hover:bg-purple-50 hover:border-purple-300 transition shadow-sm"
          >
            <History size={20} />
            <span className="font-medium">History</span>
          </button>

          {hasAnswers && (
            <>
              <button
                onClick={onNavigateToTasks}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-white border-2 border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50 hover:border-indigo-300 transition shadow-sm"
              >
                <CheckSquare size={20} />
                <span className="font-medium">View Tasks</span>
              </button>

              <button
                onClick={onDownloadPDF}
                disabled={downloadingPDF}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-white border-2 border-green-200 text-green-700 rounded-lg hover:bg-green-50 hover:border-green-300 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={20} />
                <span className="font-medium">{downloadingPDF ? 'Generating...' : 'Download'}</span>
              </button>
            </>
          )}

          {!viewingHistoricalAssessment && hasAnswers && !isViewer && (
            <button
              onClick={onRetakeAssessment}
              className="flex items-center justify-center space-x-2 px-4 py-3 bg-white border-2 border-orange-200 text-orange-700 rounded-lg hover:bg-orange-50 hover:border-orange-300 transition shadow-sm"
            >
              <RefreshCw size={20} />
              <span className="font-medium">Retake</span>
            </button>
          )}

          <button
            onClick={onShowScoringGuide}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-white border-2 border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition shadow-sm"
          >
            <HelpCircle size={20} />
            <span className="font-medium">Scoring Guide</span>
          </button>
        </div>
      </div>

      {/* Show "Start Assessment" if no answers yet */}
      {!hasAnswers && !isViewer && (
        <div className="bg-white rounded-lg border-2 border-blue-200 p-8 text-center mb-6">
          <div className="mb-4">
            <FileText size={48} className="mx-auto text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Ready to assess your business attractiveness?
          </h2>
          <p className="text-gray-600 mb-6">
            Complete our comprehensive {totalQuestions}-question assessment to identify gaps and opportunities
          </p>
          <button
            onClick={onStartAssessment}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition"
          >
            <PlayCircle size={20} className="mr-2" />
            Start Assessment
          </button>
        </div>
      )}
    </>
  );
};

GapAnalysisHeader.propTypes = {
  viewingHistoricalAssessment: PropTypes.bool.isRequired,
  assessment: PropTypes.shape({
    answered_questions: PropTypes.number,
    created_at: PropTypes.string
  }),
  onBackToCurrent: PropTypes.func.isRequired,
  onViewHistory: PropTypes.func.isRequired,
  onNavigateToTasks: PropTypes.func.isRequired,
  onDownloadPDF: PropTypes.func.isRequired,
  downloadingPDF: PropTypes.bool.isRequired,
  onRetakeAssessment: PropTypes.func.isRequired,
  onShowScoringGuide: PropTypes.func.isRequired,
  onStartAssessment: PropTypes.func.isRequired,
  totalQuestions: PropTypes.number.isRequired,
  isViewer: PropTypes.bool
};

export default GapAnalysisHeader;
